import { createSelector } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import type { AppNotificationPriority } from '../AppNotificationsMonitorContainer';
import type { AppNotificationStateItem } from './AppNotificationsSlice';

/** Sort Notifications by descending severity, then timestamp */
function sortNotificationsBySeverityAndTimestamp(a: AppNotificationStateItem, b: AppNotificationStateItem): number {
  const sortIndexBySeverity = {
    High: 3,
    Medium: 2,
    Low: 1,
  };

  const aSeverity = sortIndexBySeverity[a.notification.Severity] ?? 0;
  const bSeverity = sortIndexBySeverity[b.notification.Severity] ?? 0;
  if (aSeverity !== bSeverity) {
    return bSeverity - aSeverity; // Higher severity first
  }
  return new Date(b.notification.Timestamp).getTime() - new Date(a.notification.Timestamp).getTime(); // Newer timestamps first
}

export const selectAllToasts = createSelector(
  (state: AppState) => state.appNotifications.notificationMap,
  (state: AppState) => state.appNotifications.isReady,
  (notificationState, isReady) => (isReady ? Object.values(notificationState) : [])
);

export const selectInboxToasts = createSelector(
  (state: AppState) => state.appNotifications.notificationMap,
  (state: AppState) => state.appNotifications.isReady,
  (notificationState, isReady) =>
    isReady ? Object.values(notificationState).filter(notification => notification?.state === 'inbox') : []
);

export const selectHighMediumPriorityToasts = createSelector(selectInboxToasts, inboxToasts =>
  [...inboxToasts]
    .filter((toast): toast is AppNotificationStateItem => Boolean(toast))
    .filter(toast => (['High', 'Medium'] as AppNotificationPriority[]).includes(toast.notification.Severity))
    .toSorted(sortNotificationsBySeverityAndTimestamp)
);

export const selectLowPriorityToasts = createSelector(
  selectInboxToasts,
  selectHighMediumPriorityToasts,
  (inboxToasts, highMediumPriority) => {
    const highMediumIds = highMediumPriority.map(toast => toast.notification.NotificationID);
    return [...inboxToasts]
      .filter((toast): toast is AppNotificationStateItem => Boolean(toast))
      .filter(toast => !highMediumIds.includes(toast.notification.NotificationID))
      .toSorted(sortNotificationsBySeverityAndTimestamp);
  }
);

/**
 * Returns the count of active notifications (not closed).
 * Returns undefined if the notifications state is not ready yet.
 */
export const selectActiveNotificationCount = createSelector(
  (state: AppState) => state.appNotifications.notificationMap,
  (state: AppState) => state.appNotifications.isReady,
  (notificationMap, isReady) =>
    isReady ? Object.values(notificationMap).filter(toast => toast && toast.state !== 'closed').length : undefined
);
